import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'

import FilterPanel, { IFilterBundle, IFilterDescriptor } from './FilterPanel'
import { FilterChipDescriptor } from './FilterChips'
import { shadows } from '../../styles/effects'
import { useStickyNavOffset } from '../../hooks/useStickyNavOffset'

interface IProps {
  filters: IFilterBundle
  prependCustom?: React.ReactNode
  prependFilters?: IFilterDescriptor[]
  extraFilters?: IFilterDescriptor[]
  extraChips?: FilterChipDescriptor[]
  extraHasActive?: boolean
  onExtraClear?: () => void
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  searchPlaceholder?: string
  countLabel: string
  actions?: React.ReactNode
  leadingActions?: React.ReactNode
}

const StickyFilterPanel: React.FC<IProps> = (props) => {
  const { sentinelRef, navOffset, isStuck } = useStickyNavOffset()

  // Publishes where the panel ends, so a table header can stick right under it
  useEffect(() => {
    const panel = sentinelRef.current?.nextElementSibling as HTMLElement | null
    if (!panel || typeof ResizeObserver === 'undefined') return undefined
    const root = document.documentElement.style
    const publish = () =>
      root.setProperty('--sticky-top', `${navOffset + panel.offsetHeight}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(panel)
    return () => {
      observer.disconnect()
      root.removeProperty('--sticky-top')
    }
  }, [navOffset])

  const hasChips =
    props.filters.chips.length > 0 || (props.extraChips?.length ?? 0) > 0

  return (
    <>
      <div ref={sentinelRef} />
      <StyledFilterPanel
        {...props}
        navOffset={navOffset}
        isStuck={isStuck}
        hasChips={hasChips}
      />
    </>
  )
}

export default StickyFilterPanel

const StyledFilterPanel = styled(FilterPanel)<{
  navOffset: number
  isStuck: boolean
  hasChips: boolean
}>`
  margin-top: 0;
  ${({ navOffset, isStuck, hasChips, theme }) => css`
    position: sticky;
    top: ${navOffset}px;
    z-index: 50;
    // Full-bleed, while the padding puts the content back on the edges of the
    // centred column it sits in, whatever that column's width.
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding: 12px calc(50vw - 50%);
    background-color: ${theme.colors.primaryLight};

    ${isStuck &&
    css`
      border-bottom: 1px solid ${theme.colors.textLightest};
      box-shadow: ${shadows.depthTwo};
    `}

    > div:first-child {
      align-items: center;
    }

    > div > div > span:first-child {
      display: none;
    }

    ${!hasChips &&
    css`
      > *:nth-child(2) {
        display: none;
      }
    `}
  `}
`
